import React, { useState } from "react";
import { toast } from "sonner";
import { Layout } from "../components/tag/Layout";
import { HeroBg, EyebrowPill } from "../components/tag/Primitives";
import { api } from "../lib/api";

const SOCIALS = ["LinkedIn", "Instagram", "X/Twitter", "Facebook", "YouTube", "TikTok", "Pinterest", "None"];

const EMAIL_LIST_OPTS = [
  "No, I don't have one",
  "Yes, under 100 subscribers",
  "Yes, 100-500 subscribers",
  "Yes, 500-2,000 subscribers",
  "Yes, over 2,000 subscribers",
];

const LM_OPTS = ["No lead magnet", "Yes, one lead magnet", "Yes, multiple lead magnets"];
const DP_OPTS = ["No, just my core service", "Yes, one product or course", "Yes, multiple products"];

const INTERP_STYLES = {
  "Blank Canvas": { bg: "#1a0808", color: "#eb5a5a" },
  "Rough Draft": { bg: "#1a0f08", color: "#ba7a30" },
  "Taking Shape": { bg: "#14140a", color: "#bba030" },
  "Almost Packaged": { bg: "#0a1a0a", color: "#3aab5a" },
  "Fully Packaged": { bg: "#1a0a08", color: "#FF6575" },
};

export default function Score() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    website: "",
    industry: "",
    offer: "",
    socials: [],
    email_list: "",
    lead_magnet: "",
    digital_products: "",
    email: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const TOTAL = 8; // 7 question steps + 1 email step
  const progress = Math.min(100, Math.round((step / TOTAL) * 100));

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleSocial = (name) => {
    setForm((f) => {
      const has = f.socials.includes(name);
      let next;
      if (name === "None") {
        next = has ? [] : ["None"];
      } else {
        next = has ? f.socials.filter((s) => s !== name) : [...f.socials.filter((s) => s !== "None"), name];
      }
      return { ...f, socials: next };
    });
  };

  const canNext = () => {
    switch (step) {
      case 1: return form.website.trim().length > 0;
      case 2: return form.industry.trim().length > 0;
      case 3: return form.offer.trim().length > 0;
      case 4: return form.socials.length > 0;
      case 5: return !!form.email_list;
      case 6: return !!form.lead_magnet;
      case 7: return !!form.digital_products;
      case 8: return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
      default: return false;
    }
  };

  const next = () => setStep((s) => Math.min(TOTAL, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/score", form);
      setResult(data);
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null);
    setStep(1);
    setForm({ website: "", industry: "", offer: "", socials: [], email_list: "", lead_magnet: "", digital_products: "", email: "" });
  };

  return (
    <Layout title="What could you build? | Free Assessment" path="/score">
      <section style={{ background: "#090909", padding: "60px 32px 32px", position: "relative", overflow: "hidden", textAlign: "center" }}>
        <HeroBg />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 720, margin: "0 auto" }}>
          <div style={{ marginBottom: 24 }}><EyebrowPill>Free Assessment</EyebrowPill></div>
          <h1 className="tag-h1" style={{ marginBottom: 14 }}>What could you build from what you already know?</h1>
          <p style={{ color: "#aaa", fontSize: 15 }}>7 quick questions. Under 3 minutes. Free.</p>
        </div>
      </section>

      <section style={{ background: "#090909", borderTop: "1px solid #1a1a1a", padding: "32px 20px 60px" }}>
        <div
          data-testid="score-container"
          style={{
            background: "#0f0f0f",
            border: "1px solid #1a1a1a",
            borderRadius: 12,
            padding: 32,
            maxWidth: 560,
            margin: "0 auto",
          }}
        >
          {!result && !submitting && (
            <>
              <div style={{ height: 3, background: "#1a1a1a", borderRadius: 2, marginBottom: 20, overflow: "hidden" }}>
                <div data-testid="score-progress" style={{ height: "100%", width: `${progress}%`, background: "#FF6575", transition: "width 0.3s ease" }} />
              </div>
              <div style={{ color: "#777", fontSize: 11, marginBottom: 20 }} data-testid="score-step-indicator">
                Step {step} of {TOTAL}
              </div>

              {step === 1 && (
                <Q label="Where do you show up online today?" sub="If nowhere yet, just type 'none'.">
                  <input data-testid="q1-input" className="tag-input" placeholder="https://yoursite.com" value={form.website} onChange={(e) => set("website", e.target.value)} />
                </Q>
              )}
              {step === 2 && (
                <Q label="What space or niche do you already know well?">
                  <input data-testid="q2-input" className="tag-input" placeholder="e.g. Family law, metabolic health, IEP advocacy" value={form.industry} onChange={(e) => set("industry", e.target.value)} />
                </Q>
              )}
              {step === 3 && (
                <Q label="What do people currently pay you to do?">
                  <input data-testid="q3-input" className="tag-input" placeholder="e.g. One-on-one coaching, legal consultations" value={form.offer} onChange={(e) => set("offer", e.target.value)} />
                </Q>
              )}
              {step === 4 && (
                <Q label="Where do you already talk about your work?">
                  {SOCIALS.map((s) => {
                    const selected = form.socials.includes(s);
                    return (
                      <button
                        type="button"
                        key={s}
                        data-testid={`social-${s}`}
                        className={`tag-option ${selected ? "selected" : ""}`}
                        onClick={() => toggleSocial(s)}
                      >
                        <span style={{ width: 14, height: 14, border: `2px solid ${selected ? "#FF6575" : "#222"}`, background: selected ? "#FF6575" : "transparent", borderRadius: 3, flexShrink: 0 }} />
                        {s}
                      </button>
                    );
                  })}
                </Q>
              )}
              {step === 5 && (
                <Q label="Do you have a list of people you email directly?">
                  <RadioGroup options={EMAIL_LIST_OPTS} value={form.email_list} onChange={(v) => set("email_list", v)} testPrefix="email-list" />
                </Q>
              )}
              {step === 6 && (
                <Q label="Do you have anything you give away that captures interest?" sub="A free guide, checklist, tool, or resource that people opt in for.">
                  <RadioGroup options={LM_OPTS} value={form.lead_magnet} onChange={(v) => set("lead_magnet", v)} testPrefix="lead-magnet" />
                </Q>
              )}
              {step === 7 && (
                <Q label="Have you packaged anything you know into something people can buy?">
                  <RadioGroup options={DP_OPTS} value={form.digital_products} onChange={(v) => set("digital_products", v)} testPrefix="digital-products" />
                </Q>
              )}
              {step === 8 && (
                <Q label="Where should we send your result?">
                  <input data-testid="q8-email" className="tag-input" type="email" placeholder="you@example.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
                  <p style={{ color: "#777", fontSize: 11, marginTop: 6 }}>No spam. Unsubscribe any time.</p>
                </Q>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
                {step > 1 && (
                  <button data-testid="score-back" type="button" className="btn-ghost" onClick={back}>Back</button>
                )}
                {step < TOTAL && (
                  <button data-testid="score-next" type="button" className="btn-primary" disabled={!canNext()} onClick={next}>
                    Next
                  </button>
                )}
                {step === TOTAL && (
                  <button data-testid="score-submit" type="button" className="btn-primary" disabled={!canNext() || submitting} onClick={submit} style={{ flex: 1, minWidth: 220 }}>
                    Get My Authority Gap Score
                  </button>
                )}
              </div>
            </>
          )}

          {submitting && (
            <div data-testid="score-loading" style={{ textAlign: "center", padding: 32 }}>
              <div style={{ display: "inline-block", width: 12, height: 12, borderRadius: "50%", background: "#FF6575" }} className="tag-pulse" />
              <p style={{ color: "#aaa", marginTop: 16 }}>Calculating your Authority Gap…</p>
            </div>
          )}

          {result && !submitting && (
            <ResultDisplay result={result} email={form.email} onRetake={reset} />
          )}
        </div>
      </section>
    </Layout>
  );
}

const Q = ({ label, sub, children }) => (
  <div>
    <h3 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>{label}</h3>
    {sub && <p style={{ color: "#888", fontSize: 11, margin: "0 0 20px" }}>{sub}</p>}
    {!sub && <div style={{ height: 14 }} />}
    {children}
  </div>
);

const RadioGroup = ({ options, value, onChange, testPrefix }) => (
  <div>
    {options.map((o) => {
      const selected = value === o;
      return (
        <button
          type="button"
          key={o}
          data-testid={`${testPrefix}-${o.slice(0, 12).replace(/\W/g, "")}`}
          onClick={() => onChange(o)}
          className={`tag-option ${selected ? "selected" : ""}`}
        >
          <span className="tag-radio-dot" />
          {o}
        </button>
      );
    })}
  </div>
);

const ResultDisplay = ({ result, email, onRetake }) => {
  const interpStyle = INTERP_STYLES[result.interpretation] || INTERP_STYLES["Taking Shape"];
  return (
    <div data-testid="score-result">
      <div style={{ fontSize: 80, fontWeight: 700, color: "#fff", letterSpacing: "-0.03em", textAlign: "center", marginBottom: 6, lineHeight: 1 }} data-testid="score-result-number">
        {result.score}
      </div>
      <div style={{ color: "#888", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "center", marginBottom: 16 }}>
        Your Build-Ready Score
      </div>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <span
          data-testid="score-interpretation"
          style={{
            display: "inline-block",
            padding: "6px 14px",
            background: interpStyle.bg,
            color: interpStyle.color,
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {result.interpretation}
        </span>
      </div>
      <div className="tag-card" style={{ marginBottom: 12 }}>
        <div style={{ color: "#FF6575", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 8 }}>YOUR BIGGEST OPPORTUNITY</div>
        <p style={{ color: "#bbb", fontSize: 13, lineHeight: 1.6, margin: 0 }} data-testid="score-biggest-gap">{result.biggest_gap}</p>
      </div>
      <div className="tag-card" style={{ marginBottom: 16 }}>
        <div style={{ color: "#FF6575", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 8 }}>WHERE TO START</div>
        <p style={{ color: "#bbb", fontSize: 13, lineHeight: 1.6, margin: 0 }} data-testid="score-next-step">{result.recommended_next_step}</p>
      </div>
      <a href="/audit" data-testid="score-cta-audit" className="btn-primary" style={{ display: "block", textAlign: "center", width: "100%", boxSizing: "border-box" }}>
        See how to package this — $49
      </a>
      <p style={{ color: "#777", fontSize: 11, textAlign: "center", marginTop: 12 }}>
        We&apos;ve also sent this to <span style={{ color: "#aaa" }}>{email}</span>. Check your inbox.
      </p>
      <button type="button" onClick={onRetake} style={{ marginTop: 14, background: "transparent", border: "none", color: "#777", fontSize: 11, cursor: "pointer", width: "100%" }} data-testid="score-retake">
        Retake the assessment
      </button>
    </div>
  );
};
